import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useRealtime, sfx } from "../lib/realtime";
import SpinWheel from "../components/SpinWheel";

// Transparent OBS overlay. filterMode: "all" | "wheel" | "objective"
export default function OBSOverlay({ filterMode = "all", preview = false }) {
  const params = useParams();
  const mode = filterMode !== "all" ? filterMode : params.kind || "all";
  const [settings, setSettings] = useState(null);
  const [view, setView] = useState(null); // {kind, ...}
  const [items, setItems] = useState([]);
  const [command, setCommand] = useState(null);
  const nonceRef = useRef(0);
  const hideTimer = useRef(null);

  useEffect(() => {
    if (!preview) {
      document.body.classList.add("overlay-body");
      document.documentElement.style.background = "transparent";
    }
    api.getSettings().then(setSettings).catch(() => {});
    return () => document.body.classList.remove("overlay-body");
  }, [preview]);

  const audio = settings?.audio || { enabled: true, volume: 0.6 };
  const resultDur = (settings?.general?.resultDuration || 6) * 1000;
  const animDur = settings?.general?.animationDuration || 5;

  const scheduleHide = useCallback((ms) => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setView(null), ms);
  }, []);

  const handle = useCallback((ev) => {
    const allowWheel = mode === "all" || mode === "wheel";
    const allowObjective = mode === "all" || mode === "objective";

    if (ev.type === "OVERLAY_HIDE" || ev.type === "OVERLAY_RESET") {
      clearTimeout(hideTimer.current);
      setView(null);
      return;
    }
    if ((ev.type === "WHEEL_SPIN" || ev.type === "SUB_PENALTY") && allowWheel) {
      clearTimeout(hideTimer.current);
      setItems(ev.items);
      nonceRef.current += 1;
      setCommand({ winnerIndex: ev.winnerIndex, nonce: nonceRef.current, duration: animDur });
      setView({ kind: "wheel", title: ev.title, result: null, username: ev.username, isSub: ev.type === "SUB_PENALTY" });
      if (audio.enabled && audio.start) sfx.start(audio.volume);
      window.__ovSpin = ev;
    }
    if (ev.type === "OBJECTIVE_DRAW" && allowObjective) {
      clearTimeout(hideTimer.current);
      setView({ kind: "objective", title: ev.title, description: ev.description, difficulty: ev.difficulty });
      if (audio.enabled && audio.result) sfx.result(audio.volume);
      scheduleHide(resultDur + 2000);
    }
    if (ev.type === "SUB_ANNOUNCE" && allowWheel) {
      clearTimeout(hideTimer.current);
      setView({ kind: "sub", username: ev.username, isTest: ev.isTest });
      if (audio.enabled) sfx.sub(audio.volume);
    }
  }, [mode, audio, animDur, resultDur, scheduleHide]);

  useRealtime(handle);

  const onRest = () => {
    const ev = window.__ovSpin;
    if (!ev) return;
    setView((v) => (v && v.kind === "wheel" ? { ...v, result: ev.result, resultDescription: ev.resultDescription } : v));
    if (audio.enabled && audio.result) sfx.result(audio.volume);
    scheduleHide(resultDur);
  };

  const pos = settings?.overlay?.position || "center";
  const posClass = {
    center: "items-center justify-center",
    "top-left": "items-start justify-start",
    "top-right": "items-start justify-end",
    "bottom-left": "items-end justify-start",
    "bottom-right": "items-end justify-end",
    top: "items-start justify-center",
    bottom: "items-end justify-center",
  }[pos] || "items-center justify-center";

  const wheelSize = settings?.overlay?.wheelSize || 460;
  const title = view?.title || settings?.overlay?.title || "SIEGE STREAM WHEEL";

  return (
    <div
      className={`${preview ? "absolute inset-0" : "w-screen h-screen"} flex ${posClass} p-10`}
      style={{ background: "transparent", overflow: "hidden" }}
      data-testid="obs-overlay-root"
    >
      {!view && preview && (
        <div className="text-slate-500 text-sm font-mono">Overlay in attesa di eventi…</div>
      )}

      {view?.kind === "sub" && (
        <div className="text-center animate-sw-pop">
          <Badge>{view.isTest ? "TEST · NUOVO ABBONATO" : "NUOVO ABBONATO"}</Badge>
          <div className="font-display font-black text-white uppercase mt-3" style={{ fontSize: 96, textShadow: "0 2px 12px rgba(0,0,0,0.95)" }}>
            @{view.username}
          </div>
        </div>
      )}

      {view?.kind === "wheel" && (
        <div className="flex flex-col items-center animate-sw-slide-up">
          {view.isSub && view.username && (
            <div className="mb-3 text-center">
              <Badge>ABBONATO @{view.username}</Badge>
            </div>
          )}
          <div className="text-center mb-3">
            <span className="font-display font-extrabold uppercase text-amber-400 tracking-wider" style={{ fontSize: 40, textShadow: "0 2px 10px rgba(0,0,0,0.95)" }}>
              🎡 {title}
            </span>
          </div>
          <div style={{ filter: "drop-shadow(0 8px 30px rgba(0,0,0,0.7))" }}>
            <SpinWheel items={items} size={wheelSize} command={command} onRest={onRest}
              onTick={() => audio.enabled && audio.tick && sfx.tick(audio.volume)} />
          </div>
          {view.result && (
            <div className="mt-5 text-center animate-sw-pop">
              <div className="inline-block bg-[#080a0f]/90 border-2 border-amber-500 rounded-xl px-8 py-5" style={{ boxShadow: "0 0 40px rgba(245,158,11,0.35)" }}>
                <div className="font-display font-black uppercase text-amber-300" style={{ fontSize: settings?.overlay?.fontSize || 56, textShadow: "0 0 24px rgba(245,158,11,0.6)" }}>
                  → {view.result}
                </div>
                {view.resultDescription ? (
                  <div className="text-white text-2xl mt-1" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.9)" }}>{view.resultDescription}</div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      )}

      {view?.kind === "objective" && (
        <div className="text-center animate-sw-pop max-w-4xl">
          <Badge color={{ easy: "#10b981", medium: "#f59e0b", hard: "#ef4444" }[view.difficulty]}>
            OBIETTIVO {view.difficulty === "easy" ? "FACILE" : view.difficulty === "medium" ? "MEDIO" : "DIFFICILE"}
          </Badge>
          <div className="inline-block bg-[#080a0f]/90 border-2 border-amber-500 rounded-2xl px-10 py-8 mt-4" style={{ boxShadow: "0 0 40px rgba(245,158,11,0.3)" }}>
            <div className="font-display font-black uppercase text-white leading-tight" style={{ fontSize: settings?.overlay?.fontSize || 56, textShadow: "0 2px 10px rgba(0,0,0,0.95)" }}>
              {view.title}
            </div>
            <div className="text-slate-200 text-2xl mt-3" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.9)" }}>{view.description}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function Badge({ children, color = "#f59e0b" }) {
  return (
    <span
      className="inline-block font-display font-extrabold uppercase tracking-widest text-[#080a0f] px-5 py-2 rounded-md text-xl"
      style={{ backgroundColor: color, boxShadow: `0 0 24px ${color}88` }}
    >
      {children}
    </span>
  );
}
